import React from "react";
import { exportCSV } from "./exportCSV";

/**
 * The payments ledger.
 *
 * Ten columns do not fit a phone, and a horizontally-scrolling table is not a
 * usable answer for a screen you hold in one hand. So the table is desktop-only
 * and a caller may pass `mobileCard` — a per-row renderer shown below `lg` in
 * its place. The toolbar, the export and the pagination are shared by both, so
 * the mobile view is the same ledger rather than a cut-down one.
 *
 * Without `mobileCard` this behaves exactly as it always did.
 */
export default function DataTable({
  columns,
  data,
  pagination,
  onPageChange,
  sort,
  setSort,
  dir,
  setDir,
  selected,
  setSelected,
  onExport,
  isExporting,
  mobileCard,
}: any) {
  const toggleSort = (key: string) => {
    if (sort === key) {
      setDir(dir === "asc" ? "desc" : "asc");
    } else {
      setSort(key);
      setDir("asc");
    }
  };

  // Page links are numerous enough to overflow a phone. The numbered ones are
  // dropped there and only prev/next survive, which is how a small screen is
  // paged anyway.
  const pageLinks: any[] = pagination?.links ?? [];

  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm">
      {/* Upper Toolbar */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 p-4 border-b border-gray-100">
        <div className="text-sm font-bold text-gray-700">
          Payment List ({pagination?.total?.toLocaleString()})
        </div>
        <button
          onClick={() => onExport ? onExport() : exportCSV(data)}
          disabled={isExporting}
          className="w-full sm:w-auto px-4 py-2.5 sm:py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-xs font-bold rounded-lg transition-all"
        >
          {isExporting ? "Exporting..." : "Export CSV"}
        </button>
      </div>

      {/* Mobile cards — the same rows, stacked. */}
      {mobileCard && (
        <div className="lg:hidden divide-y divide-gray-100">
          {data.length === 0 && (
            <p className="px-4 py-10 text-center text-sm font-medium text-gray-400">
              No payments match these filters.
            </p>
          )}
          {data.map((row: any) => (
            <div key={row.id} className="p-4">{mobileCard(row)}</div>
          ))}
        </div>
      )}

      {/* Table Container with Fixed Height for Sticky Header */}
      <div className={`overflow-auto max-h-[650px] relative ${mobileCard ? "hidden lg:block" : ""}`}>
        <table className="w-full text-left border-separate border-spacing-0">
          <thead className="relative z-20">
            <tr>
              {/* Sticky Checkbox Header */}
              <th className="sticky top-0 z-30 bg-gray-100 p-4 border-b border-gray-200">
                <input
                  type="checkbox"
                  className="rounded border-gray-300"
                  onChange={(e) =>
                    setSelected(
                      e.target.checked ? data.map((d: any) => d.id) : []
                    )
                  }
                  checked={data.length > 0 && selected.length === data.length}
                />
              </th>

              {/* Sticky Data Headers */}
              {columns.map((c: any) => (
                <th
                  key={c.key}
                  className={`sticky top-0 z-30 bg-gray-100 px-4 py-4 text-xs font-bold text-gray-600 uppercase tracking-tight border-b border-gray-200 transition-colors ${
                    c.sortable === false ? "cursor-default" : "cursor-pointer hover:bg-gray-200"
                  }`}
                  onClick={() => c.sortable !== false && toggleSort(c.key)}
                >
                  <div className="flex items-center gap-1">
                    {c.header}
                    {c.sortable !== false && sort === c.key && (
                      <span className="text-blue-600">
                        {dir === "asc" ? "▲" : "▼"}
                      </span>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>

          <tbody className="bg-white">
            {data.map((row: any) => (
              <tr
                key={row.id}
                className="group even:bg-gray-50/80 hover:bg-blue-50 transition-colors"
              >
                <td className="p-4 border-b border-gray-100">
                  <input
                    type="checkbox"
                    className="rounded border-gray-300 text-blue-600"
                    checked={selected.includes(row.id)}
                    onChange={() => {
                      setSelected(
                        selected.includes(row.id)
                          ? selected.filter((x: any) => x !== row.id)
                          : [...selected, row.id]
                      );
                    }}
                  />
                </td>
                {columns.map((c: any) => (
                  <td
                    key={c.key}
                    className="px-4 py-4 text-sm text-gray-700 border-b border-gray-100"
                  >
                    {c.accessor ? c.accessor(row) : row[c.key] || "—"}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination Footer */}
      <div className="p-4 bg-gray-50 border-t border-gray-200 rounded-b-xl flex flex-col sm:flex-row gap-3 justify-between items-center">
        <span className="text-xs text-gray-500 font-medium">
          Showing {pagination?.from} to {pagination?.to} of {pagination?.total}
        </span>

        <div className="flex flex-wrap justify-center gap-1">
          {pageLinks.map((link: any, i: number) => {
            const isPrev = link.label.includes("Prev");
            const isNext = link.label.includes("Next");
            return (
              <button
                key={i}
                disabled={!link.url}
                onClick={() =>
                  onPageChange(
                    isNext
                      ? pagination.current_page + 1
                      : isPrev
                      ? pagination.current_page - 1
                      : parseInt(link.label)
                  )
                }
                className={`px-3 py-1.5 text-xs font-bold border rounded-md transition-all ${
                  link.active
                    ? "bg-blue-600 text-white border-blue-600 shadow-sm"
                    : "bg-white text-gray-600 border-gray-300 hover:border-blue-400"
                } ${!link.url && "opacity-40 cursor-not-allowed"} ${
                  // Numbered links are hidden on phones; prev/next are enough.
                  isPrev || isNext ? "" : "hidden sm:inline-block"
                }`}
              >
                {isPrev ? "←" : isNext ? "→" : link.label}
              </button>
            );
          })}
          {/* What the numbered links say on desktop, said in words on mobile. */}
          {pagination?.current_page != null && (
            <span className="sm:hidden px-3 py-1.5 text-xs font-bold text-gray-500">
              Page {pagination.current_page} of {pagination.last_page}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
